/**
 * score.js — Persistent win tally for Sky-Drop.
 *
 * Stores an all-time record of match wins in localStorage, bucketed by what was
 * actually beaten: 'local' and 'online' by mode, and vs-AI results by mode AND
 * difficulty tier. Pooling the AI tiers would make the number meaningless —
 * beating the easy opponent and beating x-hard are not the same result, and
 * with four tiers most players will meet several. The landing screen shows the
 * selected difficulty directly beside the record, so the split reads naturally
 * there rather than needing to be explained.
 *
 * Every accessor fails soft: if localStorage is unavailable or the stored value
 * is corrupt, callers get a fresh zeroed record instead of an exception —
 * scorekeeping must never break the game.
 */

import { isDifficulty } from './ai';

const STORAGE_KEY = 'skydrop_scores';

/** @returns {{player1: number, player2: number}} a fresh zeroed record. */
function emptyRecord() {
  return { player1: 0, player2: 0 };
}

/** Valid mode buckets. Unknown modes fall back to 'local'. */
const MODES = ['local', 'ai', 'online'];

/**
 * Which stored bucket a result belongs in.
 *
 * @param {string} mode       'local' | 'ai' | 'online'.
 * @param {string} [difficulty] AI tier id; only consulted for mode 'ai'.
 * @returns {string} bucket name, e.g. 'local' or 'ai-hard'.
 */
function bucketFor(mode, difficulty) {
  if (mode === 'ai') {
    return `ai-${isDifficulty(difficulty) ? difficulty : 'medium'}`;
  }
  return MODES.includes(mode) ? mode : 'local';
}

/**
 * Read the raw score object from localStorage.
 *
 * Deliberately NOT normalised to a fixed set of buckets: the bucket names now
 * depend on the difficulty tiers, so enumerating them here would mean this
 * module had to be edited every time one was added. Callers normalise the one
 * bucket they asked for instead.
 *
 * @returns {object} bucket name -> {player1, player2}; {} if unreadable.
 */
function loadScores() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
}

/**
 * Persist the score object. Silently ignores storage failures (e.g. private
 * browsing quota limits).
 * @param {object} scores
 */
function saveScores(scores) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
  } catch (err) {
    // No-op: an unwritable score is not worth interrupting play for.
  }
}

/**
 * Get the win record for one mode (and, vs AI, one difficulty tier).
 * @param {'local'|'ai'|'online'} mode
 * @param {string} [difficulty] AI tier id; only used for mode 'ai'.
 * @returns {{player1: number, player2: number}}
 */
export function getRecord(mode, difficulty) {
  const scores = loadScores();
  return { ...emptyRecord(), ...(scores[bucketFor(mode, difficulty)] || {}) };
}

/**
 * Increment and persist the winner's tally.
 * @param {'local'|'ai'|'online'} mode   Which bucket to credit.
 * @param {'player1'|'player2'} winner
 * @param {string} [difficulty] AI tier id; only used for mode 'ai'.
 * @returns {{player1: number, player2: number}} the updated record.
 */
export function recordWin(mode, winner, difficulty) {
  if (winner !== 'player1' && winner !== 'player2') {
    return getRecord(mode, difficulty);
  }
  const bucket = bucketFor(mode, difficulty);
  const scores = loadScores();
  scores[bucket] = { ...emptyRecord(), ...(scores[bucket] || {}) };
  scores[bucket][winner] += 1;
  saveScores(scores);
  return scores[bucket];
}

/**
 * Clear one record.
 * @param {'local'|'ai'|'online'} mode
 * @param {string} [difficulty] AI tier id; only used for mode 'ai'.
 * @returns {{player1: number, player2: number}} the (now zeroed) record.
 */
export function resetRecord(mode, difficulty) {
  const scores = loadScores();
  scores[bucketFor(mode, difficulty)] = emptyRecord();
  saveScores(scores);
  return emptyRecord();
}
